"use client";

import dynamic from "next/dynamic";
import { useEffect, useMemo, useRef, useState } from "react";
import { NODE_KIND_COLORS, type GraphData, type GraphLink, type GraphNode } from "@/stores/graph-store";

const ForceGraph2D = dynamic(() => import("react-force-graph-2d"), { ssr: false });

type LibNode = {
  id?: string | number;
  name?: string;
  group?: number;
  project?: string;
  kind?: string;
  x?: number;
  y?: number;
  [key: string]: unknown;
};

interface ForceGraphCanvasProps {
  graphData: GraphData;
  width?: number;
  height?: number;
  highlightedNodeIds?: Set<string>;
  highlightedLinks?: Set<GraphLink>;
  onNodeClick?: (node: GraphNode) => void;
  onNodeHover?: (node: GraphNode | null) => void;
  onRecenter?: (recenter: () => void) => void;
  cooldownTicks?: number;
  showLabelsAtZoom?: number;
  nodeRelSize?: number;
  className?: string;
  variant?: "default" | "embedded";
}

/**
 * Wrapper único de `react-force-graph-2d`. Usado tanto por `GraphView` (pane)
 * como por `WebModeView` (vista completa).
 */
export default function ForceGraphCanvas({
  graphData,
  width,
  height,
  highlightedNodeIds,
  highlightedLinks,
  onNodeClick,
  onNodeHover,
  onRecenter,
  cooldownTicks = 120,
  showLabelsAtZoom = 1.2,
  nodeRelSize = 5,
  className,
  variant = "default",
}: ForceGraphCanvasProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const graphRef = useRef<unknown>(null);
  const [dimensions, setDimensions] = useState({
    width: width ?? 800,
    height: height ?? 500,
  });
  const [isDark, setIsDark] = useState(false);

  useEffect(() => {
    const checkDark = () => setIsDark(document.documentElement.classList.contains("dark"));
    checkDark();
    const observer = new MutationObserver(checkDark);
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ["class"] });
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const updateSize = () => {
      if (!containerRef.current) return;
      const rect = containerRef.current.getBoundingClientRect();
      setDimensions({
        width: width ?? rect.width,
        height: height ?? (rect.height || 500),
      });
    };
    window.addEventListener("resize", updateSize);
    updateSize();
    const timer = setTimeout(updateSize, 100);
    return () => {
      window.removeEventListener("resize", updateSize);
      clearTimeout(timer);
    };
  }, [width, height]);

  const recenter = () => {
    const g = graphRef.current as { zoomToFit?: (ms: number, padding: number) => void } | null;
    if (g?.zoomToFit) g.zoomToFit(400, 50);
  };

  useEffect(() => {
    onRecenter?.(recenter);
  }, [onRecenter]);

  const colors = useMemo(
    () => ({
      nodeBase: isDark ? "#64748b" : "#94a3b8",
      nodeHighlight: "#ef4444",
      nodeText: isDark ? "rgba(248, 250, 252, 0.8)" : "rgba(15, 23, 42, 0.8)",
      nodeTextHighlight: "#ef4444",
      linkBase: isDark ? "rgba(51, 65, 85, 0.4)" : "rgba(226, 232, 240, 0.8)",
      linkHighlight: "#ef4444",
      linkForward: isDark ? "rgba(16, 185, 129, 0.6)" : "rgba(16, 185, 129, 0.7)", // emerald: outgoing
      linkBackward: isDark ? "rgba(59, 130, 246, 0.6)" : "rgba(59, 130, 246, 0.7)", // blue: incoming
      linkBidirectional: isDark ? "rgba(239, 68, 68, 0.7)" : "rgba(239, 68, 68, 0.8)", // red: bidirectional
    }),
    [isDark],
  );

  const getLinkColor = (direction: string | undefined, isHighlighted: boolean): string => {
    if (isHighlighted) return colors.linkHighlight;
    switch (direction) {
      case "forward":
        return colors.linkForward;
      case "backward":
        return colors.linkBackward;
      case "bidirectional":
        return colors.linkBidirectional;
      default:
        return colors.linkBase;
    }
  };

  const getNodeColor = (kind: string | undefined, isHighlighted: boolean): string => {
    if (isHighlighted) return colors.nodeHighlight;
    if (kind && kind in NODE_KIND_COLORS) {
      return NODE_KIND_COLORS[kind as keyof typeof NODE_KIND_COLORS];
    }
    return colors.nodeBase;
  };

  const containerClass =
    className ??
    (variant === "embedded"
      ? "flex-1 w-full h-full min-h-[400px]"
      : "w-full h-full min-h-[400px] flex-1 relative bg-background border border-border/30 rounded-2xl overflow-hidden");

  return (
    <div ref={containerRef} className={containerClass}>
      <ForceGraph2D
        ref={graphRef as never}
        width={dimensions.width}
        height={dimensions.height}
        graphData={graphData as never}
        nodeLabel="name"
        nodeColor={(node: unknown) => {
          const libNode = node as LibNode;
          const isHighlighted = libNode.id !== undefined && highlightedNodeIds?.has(String(libNode.id));
          return getNodeColor(libNode.kind, !!isHighlighted);
        }}
        linkColor={(link: unknown) => {
          const l = link as GraphLink;
          return getLinkColor(l.direction, highlightedLinks?.has(l) ?? false);
        }}
        linkWidth={(link: unknown) => (highlightedLinks?.has(link as GraphLink) ? 2.5 : 1.2)}
        linkDirectionalArrowLength={4}
        linkDirectionalArrowRelPos={0.95}
        linkDirectionalArrowColor={(link: unknown) => {
          const l = link as GraphLink;
          return getLinkColor(l.direction, highlightedLinks?.has(l) ?? false);
        }}
        nodeRelSize={nodeRelSize}
        cooldownTicks={cooldownTicks}
        nodeCanvasObject={(node: unknown, ctx: CanvasRenderingContext2D, globalScale: number) => {
          const libNode = node as LibNode;
          const x = libNode.x ?? 0;
          const y = libNode.y ?? 0;
          const isHighlighted = libNode.id !== undefined && highlightedNodeIds?.has(String(libNode.id));
          const label = String(libNode.name ?? "");
          const fontSize = 11 / globalScale;
          const nodeColor = getNodeColor(libNode.kind, !!isHighlighted);

          ctx.beginPath();
          ctx.arc(x, y, 5, 0, 2 * Math.PI, false);
          ctx.fillStyle = nodeColor;
          ctx.fill();

          if (isHighlighted) {
            ctx.lineWidth = 1.5 / globalScale;
            ctx.strokeStyle = isDark ? "#ffffff" : "#000000";
            ctx.stroke();
          }

          if (globalScale > showLabelsAtZoom || isHighlighted) {
            ctx.textAlign = "center";
            ctx.textBaseline = "middle";
            ctx.fillStyle = isHighlighted ? colors.nodeTextHighlight : colors.nodeText;
            ctx.font = `${fontSize}px Inter, sans-serif`;
            ctx.fillText(label, x, y + 11);
          }
        }}
        onNodeHover={onNodeHover as never}
        onNodeClick={onNodeClick as never}
      />
    </div>
  );
}
